import { ArrowLeftIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@apollo/client'
import { SUBMIT_EOD_SESSION } from '../../../../graphql/production-mutations'
import toast from 'react-hot-toast'
import type { RecipeLedgerRow } from '../RecipeLogStep'
import { floatsEqual, toBatch } from '../../utils'

interface SummaryStepProps {
  recipes: RecipeLedgerRow[]
  session: any
  canLog: boolean
  onBack: () => void
  onSessionUpdate: (updated: any) => void
  storedYesterday: Record<number, number>
}

const fmtQty = (n: number, unit: string, outputQty?: number | null) => {
  if (n <= 0) return <span className="text-gray-300">—</span>
  const batch = toBatch(n, outputQty)
  return (
    <>
      <span>{n} {unit}</span>
      {batch !== null && (
        <span className="block text-xs text-gray-400">{batch} batch</span>
      )}
    </>
  )
}

const VarianceCell = ({ value, unit }: { value: number; unit: string }) => {
  const cls = value < 0 ? 'text-red-700 font-semibold' : value > 0 ? 'text-yellow-700' : 'text-green-700'
  return <span className={cls}>{value === 0 ? '0' : `${value > 0 ? '+' : ''}${value} ${unit}`}</span>
}

const SummaryStep = ({ recipes, session, canLog, onBack, onSessionUpdate, storedYesterday }: SummaryStepProps) => {
  const negativeVarianceCount = recipes.filter((r) => r.variance_quantity < 0).length

  /** Recipes where stored-yesterday > 0 and reheated differs from it. */
  const reheatedMismatches = recipes.filter((r) => {
    const stored = storedYesterday[r.raw_material_id]
    return stored != null && stored > 0 && !floatsEqual(r.reheated_quantity, stored)
  })

  const [submitSession, { loading: submitting }] = useMutation(SUBMIT_EOD_SESSION, {
    onCompleted: (data) => {
      onSessionUpdate(data.submitEodSession)
      toast.success('End-of-shift session submitted')
    },
    onError: (e) => toast.error(e.message),
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Summary</h2>
        <p className="mt-1 text-sm text-gray-500">
          Review today's inventory before submitting. Check that the variance column looks right.
        </p>
      </div>

      {negativeVarianceCount > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">{negativeVarianceCount} recipe{negativeVarianceCount > 1 ? 's have' : ' has'} negative variance.</span>{' '}
            This means more was consumed than accounted for. Consider going back to check your entries.
          </p>
        </div>
      )}

      {reheatedMismatches.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">{reheatedMismatches.length} recipe{reheatedMismatches.length > 1 ? 's were' : ' was'} reheated in a different amount than stored last night.</span>{' '}
            {reheatedMismatches.map((r) => {
              const stored = storedYesterday[r.raw_material_id]
              const diff = r.reheated_quantity - stored
              return (
                <span key={r.raw_material_id} className="block mt-0.5">
                  {r.raw_material_name}: stored {stored} {r.unit}, reheated {r.reheated_quantity} {r.unit}{' '}
                  ({diff > 0 ? `+${diff}` : diff} {r.unit})
                </span>
              )
            })}
          </p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipe</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cooked</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Reheated</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">POS</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Stored</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Wasted</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Expired</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Staff</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-bold">Variance</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {recipes.map((r) => {
              return (
                <tr key={r.raw_material_id} className={r.variance_quantity < 0 ? 'bg-red-50' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-medium text-gray-900">{r.raw_material_name}</span>
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">{r.unit}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700">{fmtQty(r.cooked_quantity, r.unit, r.output_quantity)}</td>
                  <td className="px-4 py-3 text-center text-gray-700">
                    {(() => {
                      const stored = storedYesterday[r.raw_material_id]
                      const mismatch = stored != null && stored > 0 && r.reheated_quantity !== stored
                      return (
                        <span className={mismatch ? 'inline-flex flex-col items-center gap-0.5' : undefined}>
                          {fmtQty(r.reheated_quantity, r.unit, r.output_quantity)}
                          {mismatch && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-amber-600">
                              {r.reheated_quantity > stored
                                ? <ArrowUpIcon className="h-3 w-3" />
                                : <ArrowDownIcon className="h-3 w-3" />}
                              {r.reheated_quantity > stored ? 'over' : 'under'}
                            </span>
                          )}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-500 bg-gray-50">{fmtQty(r.sold_quantity, r.unit)}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{fmtQty(r.stored_quantity, r.unit, r.output_quantity)}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{fmtQty(r.wasted_quantity, r.unit, r.output_quantity)}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{fmtQty(r.expired_quantity, r.unit, r.output_quantity)}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{fmtQty(r.consumed_staff_quantity, r.unit, r.output_quantity)}</td>
                  <td className="px-4 py-3 text-center whitespace-nowrap"><VarianceCell value={r.variance_quantity} unit={r.unit} /></td>
                  <td className="px-4 py-3 text-center whitespace-nowrap text-gray-700">{r.current_stock} {r.unit}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        {canLog && session && (
          <button
            onClick={() => submitSession({ variables: { id: session.id } })}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500 disabled:opacity-50"
          >
            <CheckCircleIcon className="h-4 w-4" />
            {submitting ? 'Submitting…' : 'Submit end of shift'}
          </button>
        )}
      </div>
    </div>
  )
}

export default SummaryStep
